/**
 * SoleadLab 2.0 - Login Modal
 *
 * Authentication modal for logging into the SoleadLab API.
 * Token + refresh cookie are managed by authStore + apiFetch.
 */

import { useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import { useAuthStore } from '../../stores/authStore';
import type { AuthUser, OrgSummary } from '../../stores/authStore';
import { apiFetch, serverLogout, ApiError } from '../../services/apiFetch';
import './LoginModal.css';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
}

export default function LoginModal({ isOpen, onClose, onLoginSuccess }: LoginModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isLoggedIn = useAuthStore(s => s.isAuthenticated);
  const authUser = useAuthStore(s => s.user);
  const login = useAuthStore(s => s.login);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Email and password are required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await apiFetch<{
        token: string;
        user: AuthUser;
        currentOrg?: OrgSummary | null;
        orgs?: OrgSummary[];
      }>('/api/v2/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), password }),
      });

      if (!data?.token) {
        throw new Error('No token in response');
      }

      login(
        data.token,
        data.user,
        data.currentOrg ?? null,
        data.orgs ?? []
      );

      setEmail('');
      setPassword('');
      setError(null);

      onLoginSuccess?.();
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message :
        err instanceof Error ? err.message : 'Login failed';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const currentUser = authUser;

  const handleLogout = async () => {
    await serverLogout();
    setError(null);
    onLoginSuccess?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Account"
      size="sm"
    >
      <div className="login-modal">
        {isLoggedIn && currentUser ? (
          <div className="login-modal__logged-in">
            <div className="login-modal__user-info">
              <div className="login-modal__avatar">
                {currentUser.email[0].toUpperCase()}
              </div>
              <div>
                <div className="login-modal__email">{currentUser.email}</div>
                <div className="login-modal__role">{currentUser.role}</div>
              </div>
            </div>
            <Button variant="danger" size="sm" fullWidth onClick={handleLogout}>
              Logout
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-modal__form">
            <p className="login-modal__description">
              Log in to enable monitoring features (upload, alarms, data sync).
            </p>

            <div className="login-modal__field">
              <label className="login-modal__label">Email</label>
              <input
                className="login-modal__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="login-modal__field">
              <label className="login-modal__label">Password</label>
              <input
                className="login-modal__input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div className="login-modal__error">
                {error}
              </div>
            )}

            <Button
              variant="primary"
              size="md"
              fullWidth
              type="submit"
              disabled={isLoading}
              loading={isLoading}
            >
              Log In
            </Button>
          </form>
        )}
      </div>
    </Modal>
  );
}
